import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase, Product, Category, ContactSubmission, NewsletterSubscriber } from "@/lib/supabase";

const StatCard = ({ icon, label, value, color, sub }: { icon: string; label: string; value: string | number; color: string; sub?: string; }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-6 flex items-start gap-4">
    <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${color} flex-shrink-0`}>
      <i className={`${icon} text-white text-xl`} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  </div>
);

const AdminDashboardPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [{ data: prods }, { data: cats }, { data: msgs }, { data: subs }] = await Promise.all([
        supabase.from("products").select("*").order("created_at", { ascending: false }),
        supabase.from("categories").select("*"),
        supabase.from("contact_submissions").select("*").order("created_at", { ascending: false }),
        supabase.from("newsletter_subscribers").select("*"),
      ]);
      setProducts(prods || []);
      setCategories(cats || []);
      setContacts(msgs || []);
      setSubscribers(subs || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const featuredCount = useMemo(() => products.filter((p) => p.is_featured).length, [products]);
  const badgeCount = useMemo(() => products.filter((p) => p.badge).length, [products]);
  const newContactCount = useMemo(() => contacts.filter((c) => c.status === "new").length, [contacts]);
  const activeSubCount = useMemo(() => subscribers.filter((s) => s.status === "active").length, [subscribers]);
  const recentProducts = useMemo(() => products.slice(0, 5), [products]);
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [categories]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Welcome back! Here&apos;s what&apos;s happening with your store.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-4 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5 mb-8">
              <StatCard icon="ri-gift-line" label="Total Products" value={products.length} color="from-[#3a8cbf] to-[#5ab0d4]" sub="Active listings" />
              <StatCard icon="ri-star-line" label="Featured" value={featuredCount} color="from-[#F78FB3] to-[#f5b8cc]" sub="On homepage" />
              <StatCard icon="ri-folder-line" label="Categories" value={categories.length} color="from-[#a8c8e8] to-[#c8e8f8]" sub="Active" />
              <StatCard icon="ri-price-tag-3-line" label="With Badges" value={badgeCount} color="from-[#f5c6a0] to-[#f8dcc0]" sub="Labelled" />
              <StatCard icon="ri-mail-line" label="New Messages" value={newContactCount} color="from-[#e05a8a] to-[#f07aa0]" sub="Unread contacts" />
              <StatCard icon="ri-mail-send-line" label="Subscribers" value={activeSubCount} color="from-green-400 to-green-500" sub="Newsletter" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-bold text-gray-900 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Recent Products</h2>
                  <Link to="/admin/products" className="text-xs font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] cursor-pointer hover:opacity-70 transition-opacity">View All</Link>
                </div>
                <div className="flex flex-col gap-3">
                  {recentProducts.map((product) => (
                    <div key={product.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <img src={product.image} alt={product.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{product.title}</p>
                        <p className="text-xs text-gray-400">{categoryMap[product.category] || product.category}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {product.is_featured && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gradient-to-r from-[#3a8cbf]/10 to-[#e05a8a]/10 text-[#3a8cbf] font-medium">Featured</span>
                        )}
                        <span className="text-sm font-bold text-gray-900">₹{product.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-bold text-gray-900 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>Categories</h2>
                  <Link to="/admin/categories" className="text-xs font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] cursor-pointer hover:opacity-70 transition-opacity">Manage</Link>
                </div>
                <div className="flex flex-col gap-3">
                  {categories.map((cat) => {
                    const catProducts = products.filter((p) => p.category === cat.id).length;
                    const pct = products.length > 0 ? Math.round((catProducts / products.length) * 100) : 0;
                    return (
                      <div key={cat.id}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-700 font-medium">{cat.name}</span>
                          <span className="text-xs text-gray-400">{catProducts} products</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] transition-all duration-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <Link to="/admin/categories" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-sm text-gray-400 hover:border-[#3a8cbf]/40 hover:text-[#3a8cbf] transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line" /> Add New Category
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-gradient-to-r from-[#3a8cbf]/5 to-[#e05a8a]/5 rounded-2xl border border-[#3a8cbf]/10 p-6">
              <h2 className="font-bold text-gray-900 text-base mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Quick Actions</h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/admin/products" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line" /> Add New Product
                </Link>
                <Link to="/admin/categories" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-folder-add-line" /> Add Category
                </Link>
                <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-external-link-line" /> View Live Store
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
